import type {
    FilterValueOfWorkOrderPriceItemCodeFilterData,
    WorkOrderChangeObjectIdInput,
    WorkOrderChangePriceItemFilterInput,
} from 'gql/graphql';

export const codeGroupHelper = (items: FilterValueOfWorkOrderPriceItemCodeFilterData[]) =>
    items?.reduce(
        (
            acc: {
                keyMap: { [key: string]: boolean };
                itemArr: any[];
            },
            { value },
        ) => {
            if (value?.uerCode) {
                if (!acc.keyMap[value.uerCode]) {
                    acc.keyMap[value.uerCode] = true;

                    acc.itemArr.push({
                        value: {
                            isParent: true,
                            ...value,
                        },
                    });

                    if (value?.code !== value?.uerCode) {
                        acc.itemArr.push({
                            value: {
                                isParent: false,
                                ...value,
                            },
                        });
                    }
                } else if (value?.code !== value?.uerCode) {
                    const isAddedItem = acc.itemArr?.find(
                        (item) => item?.value?.uerCode === value.uerCode && item?.value?.code === value.code,
                    );

                    if (!isAddedItem) {
                        acc.itemArr.push({
                            value: {
                                isParent: false,
                                ...value,
                            },
                        });
                    }
                }
            }

            return acc;
        },
        {
            keyMap: {},
            itemArr: [],
        },
    ).itemArr;

export const codeWhereVariablesGetter = (searchStr: string) => ({
    value: {
        or: [
            {
                uerCode: { containsi: searchStr },
            },
            {
                code: { containsi: searchStr },
            },
        ],
    },
});

export const getWhereFromFilter = (name: string, values: any[]): WorkOrderChangePriceItemFilterInput => {
    if (!values?.length) {
        if (name === 'code') {
            return { or: undefined };
        }

        return {
            [name]: undefined,
        };
    }

    if (name === 'code') {
        return {
            or: values?.map(({ uerCode, code, isUer }: { uerCode: string; code: string; isUer: boolean }) => ({
                and: [
                    {
                        uerCode: { eq: uerCode },
                    },
                    {
                        code: {
                            eq: isUer ? uerCode : code,
                        },
                    },
                ],
            })),
        };
    }

    return {
        [name]: {
            in: values,
        },
    };
};

interface IObjectFilterData {
    rootObjectUniqueId?: string | null;
    objectUniqueId?: string | null;
    workOrderRootObjectId: number | null | undefined;
    workOrderChangeRootObjectId: number | null | undefined;
    workOrderObjectId?: number | null;
    workOrderChangeObjectId?: number | null;
}

export const getWhereVariables = (
    objectFilterData: IObjectFilterData | null,
    tableFilterData: WorkOrderChangePriceItemFilterInput | undefined,
): {
    where: WorkOrderChangePriceItemFilterInput;
} | null => {
    let objectFilterWhere: WorkOrderChangePriceItemFilterInput[] = [];

    const filtersWhere = (
        Object.values(tableFilterData ?? {}).filter(Boolean).length ? [tableFilterData] : []
    ) as WorkOrderChangePriceItemFilterInput[];

    const rootUid = objectFilterData?.rootObjectUniqueId?.trim();
    const leafUid = objectFilterData?.objectUniqueId?.trim();

    if (rootUid && leafUid) {
        objectFilterWhere = [
            { rootObjectUniqueId: { eq: rootUid } },
            {
                or: [
                    {
                        objectUniqueId: {
                            eq: leafUid,
                        },
                    },
                    {
                        isRootObject: {
                            eq: true,
                        },
                    },
                ],
            },
        ];
    } else if (rootUid) {
        objectFilterWhere = [{ rootObjectUniqueId: { eq: rootUid } }];
    }

    const hasActive = Boolean(objectFilterWhere?.length) || Boolean(filtersWhere?.length);

    if (hasActive) {
        return {
            where: {
                and: [...objectFilterWhere, ...filtersWhere],
            },
        };
    }

    return null;
};

export const getWhereFilterVariables = (
    objFilterData: IObjectFilterData | null,
): {
    inputRootObject?: WorkOrderChangeObjectIdInput;
    inputObject?: WorkOrderChangeObjectIdInput;
} | null => {
    if (objFilterData && 'workOrderObjectId' in objFilterData) {
        return {
            inputRootObject: {
                workOrderChangeObjectId: objFilterData?.workOrderChangeRootObjectId,
                workOrderObjectId: objFilterData?.workOrderRootObjectId,
            },
            inputObject: {
                workOrderChangeObjectId: objFilterData?.workOrderChangeObjectId,
                workOrderObjectId: objFilterData?.workOrderObjectId,
            },
        };
    }
    if (objFilterData && 'workOrderRootObjectId' in objFilterData) {
        return {
            inputRootObject: {
                workOrderChangeObjectId: objFilterData?.workOrderChangeRootObjectId,
                workOrderObjectId: objFilterData?.workOrderRootObjectId,
            },
        };
    }

    return null;
};
